import React, {useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {Link} from "react-router-dom";
import DeleteModal from "../../components/Common/DeleteModal";
import * as Yup from "yup";
import {useFormik} from "formik";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import axios from "axios"; import { handleAuthError } from "../../utils";
import {baseUrl} from '../../Network';
import withAuth from "../withAuth";
import {
    Col,
    Container,
    Row,
    Modal,
    ModalHeader,
    ModalBody,
    Form,
    Label,
    Input,
    FormFeedback,
} from "reactstrap";
import {toast, ToastContainer} from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PaginationControls from "../../components/Common/PaginationControls";
import { useConfirm } from "../../contexts/ConfirmContext";

function toSubjectList(value) {
    if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean);
    if (typeof value === "string" && value.trim()) {
        try {
            return toSubjectList(JSON.parse(value));
        } catch (error) {
            return value.split(",").map((item) => item.trim()).filter(Boolean);
        }
    }
    if (value && typeof value === "object") return toSubjectList(Object.values(value));
    return [];
}

function toSubPackageList(value) {
    if (Array.isArray(value)) return value;
    if (typeof value === "string" && value.trim()) {
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }
    return [];
}

const ManagePackage = () => {
    document.title = "Manage Package | RYD Admin";
    const { showConfirm } = useConfirm();

    const [packages, setPackages] = useState([]);
    const [timeGroup, setTimeGroup] = useState([]);
    const [cohorts, setCohorts] = useState([]);
    const [subPackages, setSubPackages] = useState([]);
    const [packagesInput, setPackagesInput] = useState({});
    const [packageData, setPackageData] = useState({});
    const [modal, setModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [bannerUrl, setBannerUrl] = useState("");
    const [packageType, setPackageType] = useState("normal");
    const [selectedSubjects, setSelectedSubjects] = useState([]);
    const privateLessonSubjects = [
        "Mathematics", "English Language", "Chemistry", "Physics", "Biology", "Further Mathematics",
        "Economics", "Accounting", "Geography", "History", "Literature in English", "Civic Education",
        "Computer Science", "Agricultural Science", "Government", "Commerce", "French", "Igbo", "Yoruba", "Basic Science",
        "Basic Technology", "Social Studies", "Business Studies", "Home Economics", "Fine Art",
    ];
    const [partners, setPartners] = useState([]);
    const isAdhoc = packageType === "adhoc";
    const isPersonal = packageType === "personal";
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 20,
        hasNextPage: false,
        hasPreviousPage: false
    });
    const [showAll, setShowAll] = useState(false);
    const [assignmentUploadingId, setAssignmentUploadingId] = useState(null);
    const [actionMenu, setActionMenu] = useState(null);
    const actionMenuRef = useRef(null);
    const assignmentInputRef = useRef(null);
    const assignmentTargetRef = useRef(null);

    const PARENT_APP_BASE =
        typeof window !== "undefined" && window.location.hostname === "localhost"
            ? "http://localhost:3001"
            : "https://parent.rydlearning.com";

    const getAdhocRegisterLink = (packageId) =>
        `${PARENT_APP_BASE}/parent/home?adhoc=${packageId}`;

    const copyAdhocLink = async (pkg) => {
        if (!pkg?.id) return;
        const link = getAdhocRegisterLink(pkg.id);
        try {
            await navigator.clipboard.writeText(link);
            toast.success("Adhoc registration link copied");
        } catch (error) {
            toast.error("Unable to copy link");
        }
    };

    useEffect(() => {
        fetchPackages();
    }, [modal, currentPage, pageSize, searchQuery]);

    const fetchPackages = async () => {
        try {
            setLoading(true);
            const limit = showAll ? 10000 : pageSize;
            const params = {
                page: currentPage,
                limit: limit,
                ...(searchQuery && { search: searchQuery })
            };
            const response = await axios.get(`${baseUrl}/admin/package/all`, { params });
            //load time group
            const responseTG = await axios.get(`${baseUrl}/admin/timegroup/all`);
            const responseCohorts = await axios.get(`${baseUrl}/admin/cohort/all`, { params: { limit: 10000 } });
            let responsePartners = { data: { data: [] } };
            try {
                responsePartners = await axios.get(`${baseUrl}/admin/partner/all`);
            } catch (partnerError) {
                handleAuthError(partnerError);
            }

            const responseData = response.data.data;
            const data = Array.isArray(responseData) ? responseData : (responseData?.data || []);
            const paginationData = responseData?.pagination || {
                currentPage: 1,
                totalPages: 1,
                totalItems: Array.isArray(data) ? data.length : 0,
                itemsPerPage: limit,
                hasNextPage: false,
                hasPreviousPage: false
            };
            const timeGroupData = Array.isArray(responseTG.data.data) ? responseTG.data.data : (responseTG.data.data?.data || []);
            const cohortData = Array.isArray(responseCohorts.data.data)
                ? responseCohorts.data.data
                : (responseCohorts.data.data?.data || []);
            setPackages((Array.isArray(data) ? data : []).map((pkg) => ({
                ...pkg,
                subjects: toSubjectList(pkg.subjects),
            })));
            setTimeGroup(Array.isArray(timeGroupData) ? timeGroupData : []);
            setCohorts(Array.isArray(cohortData) ? cohortData : []);
            const partnerData = responsePartners.data?.data;
            const partnerList = Array.isArray(partnerData) ? partnerData : (partnerData?.data || []);
            setPartners(Array.isArray(partnerList) ? partnerList : []);
            setPagination(paginationData);
            setLoading(false);
        } catch (error) { handleAuthError(error)
            console.error("Error:", error);
            setLoading(false);
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (size) => {
        if (size === 'all') {
            setShowAll(true);
            setPageSize(10000);
        } else {
            setShowAll(false);
            setPageSize(parseInt(size));
            setCurrentPage(1);
        }
    };

    const goToNextPage = () => {
        if (pagination.hasNextPage) {
            setCurrentPage(currentPage + 1);
        }
    };

    const goToPreviousPage = () => {
        if (pagination.hasPreviousPage) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
    };

    const toggle = () => {
        setModal(!modal);
    };

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            title: packageData.title || "",
            name: packageData.name || "",
            description: packageData.description || "",
            timeGroupId: packageData.timeGroupId || "",
            level: packageData.level || "",
            imageUrl: packageData.imageUrl || "",
            weekDuration: packageData.weekDuration || "",
            amount: packageData.amount || "",
            altAmount: packageData.altAmount || "",
            minAge: packageData.minAge || "",
            maxAge: packageData.maxAge || "",
            day: packageData.day === 0 || packageData.day ? packageData.day : "",
            time: packageData.time === 0 || packageData.time ? packageData.time : "",
            cohortId: packageData.cohortId || "",
            partnerId: packageData.partnerId || packageData.partner?.id || "",
            status: typeof packageData.status === "boolean" ? packageData.status : true,
            practiceLink: packageData.practiceLink || "",
        },
        validationSchema: Yup.object({
            title: isPersonal ? Yup.string().nullable() : Yup.string().required("Please enter the Title"),
            name: Yup.string().required("Please enter the Name"),
            description: isPersonal ? Yup.string().nullable() : Yup.string().required("Please enter a description"),
            timeGroupId: isAdhoc
                ? Yup.number().required("Please choose a time group for the adhoc package")
                : Yup.number().optional(),
            level: isPersonal ? Yup.mixed().nullable() : Yup.number().required("Please enter a level"),
            imageUrl: isPersonal ? Yup.string().nullable() : Yup.string().required("Please enter an image URL"),
            weekDuration: isPersonal ? Yup.mixed().nullable() : Yup.number().required("Please enter the program duration number of weeks"),
            partnerId: isPersonal ? Yup.number().required("Please select a partner") : Yup.mixed().nullable(),
            amount: isPersonal ? Yup.mixed().nullable() : Yup.number().required("Please enter the amount"),
            altAmount: Yup.number().required(isPersonal ? "Please enter the price per session (NGN)" : "Please enter the alternative amount"),
            minAge: isPersonal ? Yup.mixed().nullable() : Yup.number().required("Please enter the minimum age"),
            maxAge: isPersonal ? Yup.mixed().nullable() : Yup.number().required("Please enter the maximum age"),
            cohortId: isAdhoc
                ? Yup.number().required("Please select a cohort for the adhoc package")
                : Yup.mixed().nullable(),
        }),
        onSubmit: async (values) => {
            const loadingToastId = toast.loading(isEdit ? "Updating package..." : "Adding new package...");
            setSubmitLoading(true);
            try {
                const packageName = values.name?.trim() || "";
                const newPackage = {
                    title: isPersonal ? packageName : values.title,
                    name: packageName,
                    description: isPersonal ? packageName : values.description,
                    timeGroupId: isPersonal ? null : (values.timeGroupId ? Number(values.timeGroupId) : null),
                    subClass: isPersonal ? [] : toSubPackageList(subPackages),
                    level: isPersonal ? null : values.level,
                    imageUrl: isPersonal ? "" : values.imageUrl,
                    weekDuration: isPersonal ? 4 : values.weekDuration,
                    amount: isPersonal ? 0 : values.amount,
                    altAmount: values.altAmount,
                    minAge: isPersonal ? 0 : values.minAge,
                    maxAge: isPersonal ? 0 : values.maxAge,
                    isAdhoc: packageType === "adhoc",
                    isPersonal: packageType === "personal",
                    partnerId: packageType === "personal" ? Number(values.partnerId) : null,
                    subjects: packageType === "personal" ? selectedSubjects : [],
                    status: Boolean(values.status),
                    day: null,
                    time: null,
                    cohortId: isAdhoc ? Number(values.cohortId) : null,
                    practiceLink: isPersonal ? null : (values.practiceLink?.trim() || null),
                };

                let response;
                if (isEdit) {
                    response = await axios.put(
                        `${baseUrl}/admin/package/edit/${packageData.id}`,
                        newPackage
                    );
                } else {
                    response = await axios.post(
                        `${baseUrl}/admin/package/create`,
                        newPackage
                    );
                }

                const responseData = response.data?.data || response.data;

                if (isEdit) {
                    const updatedPackages = packages.map((pkg) =>
                        pkg.id === packageData.id ? {...pkg, ...responseData} : pkg
                    );
                    setPackages(updatedPackages);
                } else {
                    setPackages([...packages, responseData]);
                }
                toast.update(loadingToastId, {
                    render: isEdit ? "Package updated successfully" : "Package created successfully",
                    type: "success",
                    isLoading: false,
                    autoClose: 2500,
                    closeOnClick: true
                });
                toggle();
            } catch (error) { handleAuthError(error)
                console.error("Error:", error);
                toast.update(loadingToastId, {
                    render: error?.response?.data?.message || "Failed to save package",
                    type: "error",
                    isLoading: false,
                    autoClose: 4000,
                    closeOnClick: true
                });
            } finally {
                setSubmitLoading(false);
            }
        },
    });

    const handlePackageClick = (pkg) => {
        setPackageData(pkg);
        setSubPackages(toSubPackageList(pkg.subClass));
        setPackagesInput({});
        setPackageType(pkg.isPersonal ? "personal" : pkg.isAdhoc ? "adhoc" : "normal");
        setSelectedSubjects(toSubjectList(pkg.subjects));
        setIsEdit(true);
        toggle();
    };

    const onClickDelete = (pkg) => {
        setPackageData(pkg);
        setDeleteModal(true);
    };

    const handleDeletePackage = async () => {
        try {
            await axios.delete(`${baseUrl}/admin/package/${packageData.id}`);
            const updatedPackages = packages.filter(
                (pkg) => pkg.id !== packageData.id
            );
            setPackages(updatedPackages);
            setDeleteModal(false);
            toast.success("Package deleted successfully");
        } catch (error) { handleAuthError(error)
            console.error("Error:", error);
            toast.error("Failed to delete package");
        }
    };

    useEffect(() => {
        if (!actionMenu) return undefined;
        const close = (event) => {
            if (actionMenuRef.current?.contains(event.target)) return;
            if (event.target.closest?.("[data-package-actions]")) return;
            setActionMenu(null);
        };
        document.addEventListener("mousedown", close);
        window.addEventListener("scroll", close, true);
        return () => {
            document.removeEventListener("mousedown", close);
            window.removeEventListener("scroll", close, true);
        };
    }, [actionMenu]);

    const toggleActionMenu = (event, pkg) => {
        const rect = event.currentTarget.getBoundingClientRect();
        setActionMenu((current) => current?.id === pkg.id ? null : {
            id: pkg.id,
            pkg,
            top: rect.bottom + 4,
            left: rect.right,
        });
    };

    const openAssignmentPicker = (pkg) => {
        assignmentTargetRef.current = pkg;
        if (assignmentInputRef.current) {
            assignmentInputRef.current.value = "";
            assignmentInputRef.current.click();
        }
    };

    const handleAssignmentFile = async (event) => {
        const selected = event.target.files?.[0];
        const pkg = assignmentTargetRef.current;
        if (!selected || !pkg?.id) return;
        const formData = new FormData();
        formData.append("file", selected);
        const replacing = Boolean(pkg.assignmentFileUrl);
        const toastId = toast.loading(replacing ? "Replacing assignment file..." : "Uploading assignment file...");
        setAssignmentUploadingId(pkg.id);
        try {
            const response = await axios.post(`${baseUrl}/admin/package/${pkg.id}/assignment`, formData);
            if (!response.data?.status) {
                throw new Error(response.data?.message || "Failed to upload assignment file");
            }
            const saved = response.data.data || {};
            setPackages((prev) => prev.map((row) => (row.id === pkg.id ? { ...row, ...saved } : row)));
            setPackageData((prev) => (prev?.id === pkg.id ? { ...prev, ...saved } : prev));
            toast.update(toastId, {
                render: replacing ? "Assignment file replaced" : "Assignment file uploaded",
                type: "success",
                isLoading: false,
                autoClose: 3000,
                closeOnClick: true,
            });
        } catch (error) {
            handleAuthError(error);
            toast.update(toastId, {
                render: error?.response?.data?.message || error.message || "Failed to upload assignment file",
                type: "error",
                isLoading: false,
                autoClose: 4000,
                closeOnClick: true,
            });
        } finally {
            setAssignmentUploadingId(null);
        }
    };

    const addSubClassItems = () => {
        if (packagesInput?.title && packagesInput?.cad && packagesInput?.ngn) {
            setSubPackages([...toSubPackageList(subPackages), packagesInput]);
            setPackagesInput({});
        } else {
            toast.warn("Cannot put an empty sub packages");
        }
    };
    const removeSubPackage = (i) => {
        showConfirm({ title: "Remove item", message: "Are you sure you want to remove this item?", variant: "danger" }).then((ok) => {
            if (!ok) return;
            if (i > -1) {
                const list = toSubPackageList(subPackages);
                const obj = list[i];
                setSubPackages(list.filter((o) => o !== obj));
            } else {
                toast.warn("Cannot remove an empty sub packages");
            }
        });
    };

    const menuPackage = actionMenu?.pkg;

    return (
        <React.Fragment>
            {actionMenu && menuPackage ? createPortal(
                <div
                    ref={actionMenuRef}
                    className="dropdown-menu show package-action-menu"
                    style={{ "--menu-top": `${actionMenu.top}px`, "--menu-left": `${actionMenu.left}px` }}
                >
                    <button type="button" className="dropdown-item" onClick={() => { setActionMenu(null); handlePackageClick(menuPackage); }}>
                        Edit
                    </button>
                    {menuPackage.isAdhoc ? (
                        <button type="button" className="dropdown-item" onClick={() => { setActionMenu(null); copyAdhocLink(menuPackage); }}>
                            Copy registration link
                        </button>
                    ) : null}
                    <button
                        type="button"
                        className="dropdown-item"
                        disabled={assignmentUploadingId === menuPackage.id}
                        onClick={() => { setActionMenu(null); openAssignmentPicker(menuPackage); }}
                    >
                        {assignmentUploadingId === menuPackage.id
                            ? "Saving..."
                            : (menuPackage.assignmentFileUrl ? "Replace assignment file" : "Upload assignment file")}
                    </button>
                    <button type="button" className="dropdown-item text-danger" onClick={() => { setActionMenu(null); onClickDelete(menuPackage); }}>
                        Delete
                    </button>
                </div>,
                document.body
            ) : null}
            <input
                ref={assignmentInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.ppt,.pptx,.html,.htm,.png,.jpg,.jpeg,application/pdf,text/html"
                style={{ display: "none" }}
                onChange={handleAssignmentFile}
            />
            <DeleteModal
                show={deleteModal}
                onDeleteClick={handleDeletePackage}
                onCloseClick={() => setDeleteModal(false)}
            />
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Dashboard" breadcrumbItem="Manage Package"/>
                    <Row>
                        <Col md={6}>
                            <div className="mb-3">
                                <h5 className="card-title">
                                    Package List{" "}
                                    <span className="text-muted fw-normal ms-2">
                    ({packages.length})
                  </span>
                                </h5>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 mb-3">
                                <div>
                                    <Link
                                        to="#"
                                        className="btn btn-light"
                                        onClick={() => {
                                            setPackageData({});
                                            setSubPackages([]);
                                            setPackagesInput({});
                                            setPackageType("normal");
                                            setSelectedSubjects([]);
                                            setIsEdit(false);
                                            toggle();
                                        }}>
                                        <i className="bx bx-plus me-1"></i> Add New Package
                                    </Link>
                                </div>
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col xl="12" className={'divTable'}>
                            {loading ? (
                                <div className="text-center mt-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : packages.length === 0 ? (
                                <div className="text-center mt-5">
                                    <h3>No data available</h3>
                                </div>
                            ) : (
                                <table className="table align-middle">
                                    <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Title</th>
                                        <th>Name</th>
                                        <th>Type</th>
                                        <th>Status</th>
                                        <th>Assignment</th>
                                        <th>Description</th>
                                        <th>Level</th>
                                        <th>SubClass</th>
                                        <th>Weeks</th>
                                        <th>Amount</th>
                                        <th>Alt.Amount</th>
                                        <th>Age Range</th>
                                        <th>Action</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {packages.map((pkg, index) => {
                                        const globalIndex = showAll 
                                            ? index 
                                            : (pagination.currentPage - 1) * pagination.itemsPerPage + index;
                                        return (
                                            <tr key={pkg.id || index}>
                                                <td>{globalIndex + 1}</td>
                                                <td>{pkg.title}</td>
                                                <td>{pkg.name}</td>
                                                <td>
                                                    {pkg.isPersonal ? (
                                                        <span className="badge bg-primary text-white">Private lesson</span>
                                                    ) : pkg.isAdhoc ? (
                                                        <span className="badge bg-warning text-dark">Adhoc</span>
                                                    ) : (
                                                        <span className="badge bg-secondary">Normal</span>
                                                    )}
                                                    {pkg.isPersonal && pkg.partner?.organizationName ? (
                                                        <div><small>{pkg.partner.organizationName}</small></div>
                                                    ) : null}
                                                </td>
                                                <td>
                                                    {pkg.status ? (
                                                        <span className="badge bg-success">Active</span>
                                                    ) : (
                                                        <span className="badge bg-danger">Inactive</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {pkg.assignmentFileUrl ? (
                                                        <span className="badge bg-success">Yes</span>
                                                    ) : (
                                                        <span className="badge bg-secondary">No</span>
                                                    )}
                                                </td>
                                                <td>{pkg.isPersonal ? "—" : pkg.description}</td>
                                                <td>{pkg.isPersonal ? "—" : pkg.level}</td>
                                                <td>{pkg.isPersonal ? "—" : (pkg?.subClass?.length || 0)}</td>
                                                <td>{pkg?.weekDuration}</td>
                                                <td>{pkg.isPersonal ? "—" : pkg?.amount}</td>
                                                <td>{pkg?.altAmount}</td>
                                                <td>{pkg.isPersonal ? "—" : `${pkg?.minAge} - ${pkg?.maxAge}`}</td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        data-package-actions
                                                        className="btn btn-link text-dark font-size-18 p-0 shadow-none"
                                                        aria-label="Package actions"
                                                        onClick={(event) => toggleActionMenu(event, pkg)}
                                                    >
                                                        <i className="bx bx-dots-vertical-rounded"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            )}
                            {!loading && packages.length > 0 && (
                                <PaginationControls
                                    currentPage={pagination.currentPage}
                                    totalPages={pagination.totalPages}
                                    totalItems={pagination.totalItems}
                                    pageSize={pagination.itemsPerPage}
                                    showAll={showAll}
                                    handlePageChange={handlePageChange}
                                    handlePageSizeChange={handlePageSizeChange}
                                    goToNextPage={goToNextPage}
                                    goToPreviousPage={goToPreviousPage}
                                    canPreviousPage={pagination.hasPreviousPage}
                                    canNextPage={pagination.hasNextPage}
                                    paginatedDataLength={packages.length}
                                />
                            )}
                            <Modal isOpen={modal} toggle={toggle}>
                                <ModalHeader toggle={toggle}>
                                    {isEdit ? "Edit Package" : "Add New Package"}
                                </ModalHeader>
                                <ModalBody>
                                    <Form onSubmit={validation.handleSubmit}>
                                        <Row>
                                            <Col xs={12}>
                                                <div className="row">
                                                    <div className="col-md-6 mb-3">
                                                        <Label className="form-label">Package Type</Label>
                                                        <Input
                                                            type="select"
                                                            value={packageType}
                                                            onChange={(e) => setPackageType(e.target.value)}
                                                        >
                                                            <option value="normal">Normal (progression)</option>
                                                            <option value="adhoc">Adhoc (one-time)</option>
                                                            <option value="personal">Private lesson</option>
                                                        </Input>
                                                        {isAdhoc && (
                                                            <small className="text-muted d-block mt-1">
                                                                Only one adhoc package can be active at a time. Attach a time group — parents select day/time. Cohort is fixed.
                                                            </small>
                                                        )}
                                                        {isPersonal && (
                                                            <small className="text-muted d-block mt-1">
                                                                Parents choose 2–4 weekly times and a grade. Price is per session.
                                                            </small>
                                                        )}
                                                    </div>
                                                    {isPersonal && (
                                                        <div className="col-md-6 mb-3">
                                                            <Label className="form-label">Partner</Label>
                                                            <Input
                                                                name="partnerId"
                                                                type="select"
                                                                onChange={validation.handleChange}
                                                                onBlur={validation.handleBlur}
                                                                value={validation.values.partnerId || ""}
                                                                invalid={validation.touched.partnerId && validation.errors.partnerId}
                                                            >
                                                                <option value="">Select partner</option>
                                                                {partners.map((partner) => (
                                                                    <option key={partner.id} value={partner.id}>
                                                                        {partner.organizationName}
                                                                    </option>
                                                                ))}
                                                            </Input>
                                                            <FormFeedback type="invalid">{validation.errors.partnerId}</FormFeedback>
                                                        </div>
                                                    )}
                                                    {isPersonal && (
                                                        <div className="col-12 mb-3">
                                                            <Label className="form-label">Subjects offered</Label>
                                                            <div className="border rounded p-2" style={{ maxHeight: 240, overflowY: "auto" }}>
                                                                {[
                                                                    ...privateLessonSubjects,
                                                                    ...selectedSubjects.filter((item) => !privateLessonSubjects.includes(item)),
                                                                ].map((item) => (
                                                                    <label key={item} className="d-flex align-items-center gap-2 mb-1">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={selectedSubjects.includes(item)}
                                                                            onChange={() => {
                                                                                setSelectedSubjects((current) => (
                                                                                    current.includes(item)
                                                                                        ? current.filter((subject) => subject !== item)
                                                                                        : [...current, item]
                                                                                ));
                                                                            }}
                                                                        />
                                                                        <span>{item}</span>
                                                                    </label>
                                                                ))}
                                                            </div>
                                                            <small className="text-muted d-block mt-1">
                                                                {selectedSubjects.length === 0
                                                                    ? "Parents only see the subjects you check here."
                                                                    : `${selectedSubjects.length} selected. Uncheck a subject to remove it.`}
                                                            </small>
                                                        </div>
                                                    )}
                                                </div>

                                                {!isPersonal && (
                                                    <div className="mb-3">
                                                        <Label className="form-label">Program Title</Label>
                                                        <Input
                                                            name="title"
                                                            type="text"
                                                            placeholder="Title"
                                                            onChange={validation.handleChange}
                                                            onBlur={validation.handleBlur}
                                                            value={validation.values.title || ""}
                                                            invalid={
                                                                validation.touched.title &&
                                                                validation.errors.title
                                                            }
                                                        />
                                                        <FormFeedback type="invalid">
                                                            {validation.errors.title}
                                                        </FormFeedback>
                                                    </div>
                                                )}

                                                <div className="mb-3">
                                                    <Label className="form-label">Program Name</Label>
                                                    <Input
                                                        name="name"
                                                        type="text"
                                                        placeholder="Title"
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.name || ""}
                                                        invalid={
                                                            validation.touched.name &&
                                                            validation.errors.name
                                                        }
                                                    />
                                                    <FormFeedback type="invalid">
                                                        {validation.errors.name}
                                                    </FormFeedback>
                                                </div>

                                                {!isPersonal && (
                                                <Row>
                                                    <div className="mb-3 col-md-6">
                                                        <Label className="form-label">Program Description</Label>
                                                        <Input
                                                            name="description"
                                                            type="text"
                                                            placeholder="Description"
                                                            onChange={validation.handleChange}
                                                            onBlur={validation.handleBlur}
                                                            value={validation.values.description || ""}
                                                            invalid={
                                                                validation.touched.description &&
                                                                validation.errors.description
                                                            }
                                                        />
                                                        <FormFeedback type="invalid">
                                                            {validation.errors.description}
                                                        </FormFeedback>
                                                    </div>
                                                    <div className="mb-3 col-md-6">
                                                        <Label className="form-label">
                                                            Choose Time Group
                                                            {isAdhoc ? " *" : ""}
                                                        </Label>
                                                        <Input
                                                            name="timeGroupId"
                                                            type="select"
                                                            placeholder="Time Group"
                                                            onChange={validation.handleChange}
                                                            onBlur={validation.handleBlur}
                                                            value={validation.values.timeGroupId || null}
                                                            invalid={
                                                                validation.touched.timeGroupId &&
                                                                validation.errors.timeGroupId
                                                            }>
                                                            <option value={null}>--Choose--</option>
                                                            {Array.isArray(timeGroup) && timeGroup.map((t, i) => <option key={i}
                                                                                             value={Number(t.id)}>{t.title}</option>)}
                                                        </Input>
                                                        <FormFeedback type="invalid">
                                                            {validation.errors.timeGroupId}
                                                        </FormFeedback>
                                                        {isAdhoc && (
                                                            <small className="text-muted d-block mt-1">
                                                                Required for adhoc. Parents pick day/time from this group (shown in their timezone).
                                                            </small>
                                                        )}
                                                    </div>
                                                </Row>
                                                )}

                                                {!isPersonal && <Row>
                                                    <div className="col-md-6 mb-3">
                                                        <Label>Level</Label>
                                                        <Input
                                                            defaultValue={1}
                                                            type="select"
                                                            name="level"
                                                            onChange={validation.handleChange}
                                                            onBlur={validation.handleBlur}
                                                            value={validation.values.level || ""}
                                                            invalid={
                                                                validation.touched.level &&
                                                                validation.errors.level
                                                            }>
                                                            <option value="0">Select Level</option>
                                                            <option selected={true} value={1}>1</option>
                                                            <option value={2}>2</option>
                                                            <option value={3}>3</option>
                                                            <option value={4}>4</option>
                                                            <option value={5}>5</option>
                                                            <option value={6}>6</option>
                                                            <option value={7}>7</option>
                                                            <option value={8}>8</option>
                                                            <option value={9}>9</option>
                                                            <option value={10}>10</option>
                                                            <option value={11}>11</option>
                                                            <option value={12}>12</option>
                                                            <option value={13}>13</option>
                                                            <option value={14}>14</option>
                                                            <option value={15}>15</option>
                                                            <option value={16}>16</option>
                                                            <option value={17}>17</option>
                                                            <option value={18}>18</option>
                                                            <option value={19}>19</option>
                                                            <option value={20}>20</option>
                                                        </Input>
                                                        <FormFeedback type="invalid">
                                                            {validation.errors.level}
                                                        </FormFeedback>
                                                    </div>

                                                    <div className="col-md-6 mb-3">
                                                        <Label className="form-label">Week Duration </Label>
                                                        <Input
                                                            type="text"
                                                            name="weekDuration"
                                                            placeholder="Number of weeks"
                                                            onChange={validation.handleChange}
                                                            onBlur={validation.handleBlur}
                                                            value={validation.values.weekDuration || ""}
                                                            invalid={validation.touched.weekDuration && validation.errors.weekDuration}
                                                        />
                                                        <FormFeedback
                                                            type="invalid">{validation.errors.weekDuration}</FormFeedback>
                                                    </div>
                                                </Row>}


                                                {!isPersonal && <div className="mb-3">
                                                    <Label className="form-label">Practice link</Label>
                                                    <Input
                                                        name="practiceLink"
                                                        type="url"
                                                        placeholder="https://studio.code.org"
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.practiceLink || ""}
                                                    />
                                                    <small className="text-muted d-block mt-1">
                                                        Base URL for the Practice button. Levels 1, 2, and 8 with section codes use Code.org (e.g. https://studio.code.org).
                                                    </small>
                                                </div>}

                                                {!isPersonal && (
                                                <div className="mb-3">
                                                    <Label className="form-label">Banner Image URL </Label>
                                                    <small style={{marginLeft: 20}}><a href={'#'}
                                                                                       onClick={() => setBannerUrl("https://i.im.ge/2024/04/19/Z2k3bq.basic-bg.jpeg")}>Basic
                                                        Banner</a> </small>
                                                    <small style={{marginLeft: 20}}><a href={'#'}
                                                                                       onClick={() => setBannerUrl("https://i.im.ge/2024/04/19/Z2kgA1.advance-bg.jpeg")}>Adv.
                                                        Banner</a> </small>
                                                    <Input
                                                        name="imageUrl"
                                                        type="text"
                                                        placeholder="Image URL"
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.imageUrl || bannerUrl}
                                                        invalid={
                                                            validation.touched.imageUrl &&
                                                            validation.errors.imageUrl
                                                        }
                                                    />
                                                    <FormFeedback type="invalid">
                                                        {validation.errors.imageUrl}
                                                    </FormFeedback>
                                                </div>
                                                )}


                                                <div className="row">
                                                    {!isPersonal && (
                                                    <div className="col-md-6 mb-3">
                                                        <Label className="form-label">Amount</Label>
                                                        <Input
                                                            name="amount"
                                                            type="number"
                                                            placeholder="CAD"
                                                            onChange={validation.handleChange}
                                                            onBlur={validation.handleBlur}
                                                            value={validation.values.amount || ""}
                                                            invalid={validation.touched.amount && validation.errors.amount}
                                                        />
                                                        <FormFeedback
                                                            type="invalid">{validation.errors.amount}</FormFeedback>
                                                    </div>
                                                    )}

                                                    <div className={`${isPersonal ? "col-12" : "col-md-6"} mb-3`}>
                                                        <Label className="form-label">{isPersonal ? "Price per session (NGN)" : "Alt Amount"}</Label>
                                                        <Input
                                                            name="altAmount"
                                                            type="number"
                                                            placeholder="NGN"
                                                            onChange={validation.handleChange}
                                                            onBlur={validation.handleBlur}
                                                            value={validation.values.altAmount || ""}
                                                            invalid={validation.touched.altAmount && validation.errors.altAmount}
                                                        />
                                                        <FormFeedback
                                                            type="invalid">{validation.errors.altAmount}</FormFeedback>

                                                    </div>
                                                </div>

                                                {!isPersonal && <div className="row">

                                                    <div className="col-md-6 mb-3">
                                                        <Label className="form-label">Min Age</Label>
                                                        <Input
                                                            name="minAge"
                                                            type="number"
                                                            placeholder="Minimum Age"
                                                            onChange={validation.handleChange}
                                                            onBlur={validation.handleBlur}
                                                            value={validation.values.minAge || ""}
                                                            invalid={
                                                                validation.touched.minAge &&
                                                                validation.errors.minAge
                                                            }
                                                        />
                                                        <FormFeedback type="invalid">
                                                            {validation.errors.minAge}
                                                        </FormFeedback>
                                                    </div>
                                                    <div className="col-md-6 mb-3">
                                                        <Label className="form-label">Max Age</Label>
                                                        <Input
                                                            name="maxAge"
                                                            type="number"
                                                            placeholder="Maximum Age"
                                                            onChange={validation.handleChange}
                                                            onBlur={validation.handleBlur}
                                                            value={validation.values.maxAge || ""}
                                                            invalid={
                                                                validation.touched.maxAge &&
                                                                validation.errors.maxAge
                                                            }
                                                        />
                                                        <FormFeedback type="invalid">
                                                            {validation.errors.maxAge}
                                                        </FormFeedback>
                                                    </div>
                                                </div>}

                                                <div className="row">
                                                    <div className="col-md-6 mb-3">
                                                        <Label className="form-label">Status</Label>
                                                        <Input
                                                            name="status"
                                                            type="select"
                                                            onChange={(e) =>
                                                                validation.setFieldValue(
                                                                    "status",
                                                                    e.target.value === "true"
                                                                )
                                                            }
                                                            value={String(Boolean(validation.values.status))}
                                                        >
                                                            <option value="true">Active</option>
                                                            <option value="false">Inactive</option>
                                                        </Input>
                                                    </div>
                                                </div>

                                                {isAdhoc && (
                                                    <div className="row border rounded p-2 mb-3 bg-light">
                                                        <div className="col-12 mb-2">
                                                            <strong>Adhoc settings</strong>
                                                        </div>
                                                        <div className="col-md-6 mb-3">
                                                            <Label className="form-label">Cohort</Label>
                                                            <Input
                                                                name="cohortId"
                                                                type="select"
                                                                onChange={validation.handleChange}
                                                                onBlur={validation.handleBlur}
                                                                value={validation.values.cohortId || ""}
                                                                invalid={validation.touched.cohortId && validation.errors.cohortId}
                                                            >
                                                                <option value="">Select Cohort</option>
                                                                {Array.isArray(cohorts) && cohorts.map((c) => (
                                                                    <option key={c.id} value={c.id}>{c.title}</option>
                                                                ))}
                                                            </Input>
                                                            <FormFeedback type="invalid">{validation.errors.cohortId}</FormFeedback>
                                                        </div>
                                                        {isEdit && packageData?.id ? (
                                                            <div className="col-12 mb-2">
                                                                <Label className="form-label">Registration link (for advert button)</Label>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <Input
                                                                        type="text"
                                                                        readOnly
                                                                        value={getAdhocRegisterLink(packageData.id)}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-primary"
                                                                        onClick={() => copyAdhocLink(packageData)}
                                                                    >
                                                                        <i className="bx bx-copy"></i>
                                                                    </button>
                                                                </div>
                                                                <small className="text-muted">
                                                                    Paste this into Manage Advert → Button Link so parents open the adhoc modal from the slider.
                                                                </small>
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                )}
                                            </Col>
                                        </Row>

                                        {isEdit && !isPersonal ? <>
                                            <Row>
                                                <Col>
                                                    <div className="col-md-12 mb-3">
                                                        <Label className="form-label">Sub Packages</Label>
                                                        <table style={{width: '100%'}}>
                                                            <tr>
                                                                <td><input value={packagesInput?.title || ""}
                                                                           onChange={e => setPackagesInput({
                                                                               ...packagesInput,
                                                                               title: e.target.value
                                                                           })} placeholder={"Title"}/></td>
                                                                <td><input type={'number'}
                                                                           value={packagesInput?.cad || ""}
                                                                           onChange={e => setPackagesInput({
                                                                               ...packagesInput,
                                                                               cad: e.target.value
                                                                           })} placeholder={"CAD"}/></td>
                                                                <td><input type={'number'}
                                                                           value={packagesInput?.ngn || ""}
                                                                           onChange={e => setPackagesInput({
                                                                               ...packagesInput,
                                                                               ngn: e.target.value
                                                                           })} placeholder={"NGN"}/></td>
                                                                <td>
                                                                    <Link
                                                                        className="text-success"
                                                                        to="#"
                                                                        onClick={
                                                                            (e) => {
                                                                                e.preventDefault()
                                                                                addSubClassItems()
                                                                            }}>
                                                                        <i className="bx bx-book-add font-size-18"></i>
                                                                    </Link>
                                                                </td>
                                                            </tr>
                                                        </table>
                                                    </div>
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col xs={12}>
                                                    <div className="col-md-12 mb-3">
                                                        <table style={{width: '100%'}}>
                                                            <tr>
                                                                <th>Title</th>
                                                                <th>CAD</th>
                                                                <th>NGN</th>
                                                                <th></th>
                                                            </tr>
                                                            {toSubPackageList(subPackages).length > 0 && toSubPackageList(subPackages).map((s, i) => <>
                                                                <tr>
                                                                    <td>{s.title}</td>
                                                                    <td>{s.cad}</td>
                                                                    <td>{s.ngn}</td>
                                                                    <td><i className="bx bx-x font-size-18"
                                                                           style={{cursor: 'pointer'}} onClick={() => {
                                                                        removeSubPackage(i)
                                                                    }}></i></td>
                                                                </tr>
                                                            </>).reverse() || <p style={{
                                                                width: '100%',
                                                                textAlign: 'left',
                                                                color: '#6e6e6e'
                                                            }}>No Data Entries</p>}
                                                        </table>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </> : null}

                                        {isEdit && packageData?.id ? (
                                            <div className="mb-3">
                                                <Label className="form-label">Assignment file</Label>
                                                {packageData.assignmentFileUrl ? (
                                                    <div className="mb-2">
                                                        <a href={packageData.assignmentFileUrl} target="_blank" rel="noopener noreferrer">
                                                            View current file
                                                        </a>
                                                    </div>
                                                ) : (
                                                    <small className="text-muted d-block mb-2">No assignment file uploaded yet.</small>
                                                )}
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-primary btn-sm"
                                                    disabled={assignmentUploadingId === packageData.id}
                                                    onClick={() => openAssignmentPicker(packageData)}
                                                >
                                                    {packageData.assignmentFileUrl ? "Upload a new file" : "Upload file"}
                                                </button>
                                                <small className="text-muted d-block mt-1">
                                                    PDF, Word, PowerPoint, HTML, or image, up to 15MB. A new upload replaces the previous file.
                                                </small>
                                            </div>
                                        ) : null}

                                        <Row>
                                            <Col>
                                                <div className="text-end">
                                                    <button
                                                        type="submit"
                                                        className="btn btn-primary save-user"
                                                        disabled={submitLoading}>
                                                        {submitLoading ? (isEdit ? "Updating..." : "Adding...") : (isEdit ? "Update" : "Add New Package")}
                                                    </button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Form>
                                </ModalBody>
                            </Modal>
                        </Col>
                    </Row>
                </Container>
            </div>
            <ToastContainer/>
        </React.Fragment>
    );
};


export default withAuth(ManagePackage);
